import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { createUserProfile, updateLastLogin, validateInvitationCode } from '../services/userService';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import bcrypt from 'bcryptjs';
import { updateUserData, getUserByUsername } from '../firebase/helpers';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

function UserAuth({ onAuthSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [transactionPassword, setTransactionPassword] = useState('');
  const [confirmTransactionPassword, setConfirmTransactionPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const { register, login, logout } = useAuth();
  const loginAttemptsRef = useRef(0);
  const lastAttemptTimeRef = useRef(null);

  // Load remember me email and login attempts from localStorage
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail && isLogin) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
    
    // Load login attempts
    const storedAttempts = localStorage.getItem('loginAttempts');
    const storedTime = localStorage.getItem('lastAttemptTime');
    if (storedAttempts) {
      loginAttemptsRef.current = parseInt(storedAttempts, 10);
    }
    if (storedTime) {
      lastAttemptTimeRef.current = parseInt(storedTime, 10);
    }
  }, [isLogin]);

  // Check for lockout
  useEffect(() => {
    const lockoutData = localStorage.getItem('loginLockout');
    if (lockoutData) {
      const { until } = JSON.parse(lockoutData);
      const lockoutTime = new Date(until);
      if (lockoutTime > new Date()) {
        setIsLocked(true);
        setLockoutUntil(lockoutTime);
      } else {
        localStorage.removeItem('loginLockout');
        localStorage.removeItem('loginAttempts');
      }
    }
  }, []);

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };


  // Rate limiting check
  const checkRateLimit = () => {
    const now = Date.now();
    const timeWindow = 15 * 60 * 1000; // 15 minutes
    const maxAttempts = 5;

    if (lastAttemptTimeRef.current && (now - lastAttemptTimeRef.current) > timeWindow) {
      // Reset if outside time window
      loginAttemptsRef.current = 0;
      lastAttemptTimeRef.current = null;
      localStorage.removeItem('loginAttempts');
      localStorage.removeItem('lastAttemptTime');
    }

    if (loginAttemptsRef.current >= maxAttempts) {
      const lockoutDuration = 15 * 60 * 1000; // 15 minutes
      const lockoutUntil = new Date(now + lockoutDuration);
      setIsLocked(true);
      setLockoutUntil(lockoutUntil);
      localStorage.setItem('loginLockout', JSON.stringify({ until: lockoutUntil.toISOString() }));
      return false;
    }

    return true;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset email sent! Please check your inbox.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setSuccess('');
      }, 3000);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError('Failed to send reset email. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        if (isLocked) {
          if (lockoutUntil && new Date(lockoutUntil) > new Date()) {
            const minutesLeft = Math.ceil((new Date(lockoutUntil) - new Date()) / 60000);
            setError(`Too many failed attempts. Please try again in ${minutesLeft} minute(s).`);
            setLoading(false);
            return;
          } else {
            setIsLocked(false);
            setLockoutUntil(null);
            localStorage.removeItem('loginLockout');
          }
        }

        if (!checkRateLimit()) {
          setError('Too many login attempts. Please try again in 15 minutes.');
          setLoading(false);
          return;
        }

        // Validate input (email or username)
        if (!email || email.trim() === '') {
          setError('Please enter your email or username');
          setLoading(false);
          return;
        }

        // Validate password
        if (!password) {
          setError('Please enter your password');
          setLoading(false);
          return;
        }

        // Check if input is email or username
        let loginEmail = email.trim();
        const isEmail = validateEmail(email);
        
        // If it's not an email, try to find user by username
        if (!isEmail) {
          try {
            const userByUsername = await getUserByUsername(email.trim());
            if (!userByUsername || !userByUsername.email) {
              setError('Invalid username or email. Please check your credentials.');
              setLoading(false);
              loginAttemptsRef.current++;
              lastAttemptTimeRef.current = Date.now();
              localStorage.setItem('loginAttempts', loginAttemptsRef.current.toString());
              localStorage.setItem('lastAttemptTime', lastAttemptTimeRef.current.toString());
              return;
            }
            loginEmail = userByUsername.email;
          } catch (error) {
            console.error('Error finding user by username:', error);
            setError('Error finding user. Please try again.');
            setLoading(false);
            loginAttemptsRef.current++;
            lastAttemptTimeRef.current = Date.now();
            localStorage.setItem('loginAttempts', loginAttemptsRef.current.toString());
            localStorage.setItem('lastAttemptTime', lastAttemptTimeRef.current.toString());
            return;
          }
        }

        const user = await login(loginEmail, password);
        
        // Reset login attempts on success
        loginAttemptsRef.current = 0;
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('lastAttemptTime');

        // Handle remember me (store the original input, whether email or username)
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // Check if account exists and is not deleted
        const { getUserProfile } = await import('../services/userService');
        const profile = await getUserProfile(user.uid);
        
        if (!profile) {
          await logout();
          setError('Your account has been deleted. You cannot access the website. Please contact support.');
          setLoading(false);
          return;
        }
        
        if (profile.isDeleted) {
          await logout();
          setError('Your account has been deleted. You cannot access the website. Please contact support.');
          setLoading(false);
          return;
        }
        
        if (profile.isActive === false) {
          await logout();
          setError('Your account has been deactivated. Please contact support.');
          setLoading(false);
          return;
        }
        
        // Update last login time
        await updateLastLogin(user.uid);
        
        // Redirect to dashboard
        if (onAuthSuccess) {
          onAuthSuccess(user);
        }
      } else {
        // Registration
        // Validate required fields
        if (!username.trim()) {
          setError('Username is required');
          setLoading(false);
          return;
        }

        if (username.length < 3) {
          setError('Username must be at least 3 characters long');
          setLoading(false);
          return;
        }

        if (!email || !validateEmail(email)) {
          setError('Please enter a valid email address');
          setLoading(false);
          return;
        }

        if (!password || password.length < 8) {
          setError('Password must be at least 8 characters long');
          setLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        // Validate transaction password
        if (!transactionPassword || transactionPassword.length < 6) {
          setError('Transaction password must be at least 6 characters long');
          setLoading(false);
          return;
        }

        if (transactionPassword !== confirmTransactionPassword) {
          setError('Transaction passwords do not match');
          setLoading(false);
          return;
        }

        // Check terms acceptance
        if (!acceptTerms) {
          setError('You must accept the terms and conditions to register');
          setLoading(false);
          return;
        }

        // Check if email is already registered
        try {
          const { fetchSignInMethodsForEmail } = await import('firebase/auth');
          const signInMethods = await fetchSignInMethodsForEmail(auth, email);
          if (signInMethods.length > 0) {
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const { db } = await import('../firebase/config');
            
            const usersRef = collection(db, 'users');
            const emailQuery = query(usersRef, where('email', '==', email.toLowerCase().trim()));
            const emailSnapshot = await getDocs(emailQuery);
            
            if (emailSnapshot.empty) {
              setError('This email was previously registered but the account has been deleted. You cannot register again. Please contact support.');
              setLoading(false);
              return;
            } else {
              const existingUser = emailSnapshot.docs[0].data();
              if (existingUser.isDeleted) {
                setError('This email was previously registered but the account has been deleted. You cannot register again. Please contact support.');
                setLoading(false);
                return;
              } else {
                setError('This email is already registered. Please login instead.');
                setLoading(false);
                return;
              }
            }
          }
        } catch (checkError) {
          console.error('Error checking existing user:', checkError);
        }

        // Validate invitation code (required)
        if (!invitationCode || invitationCode.trim() === '') {
          setError('Invitation code is required');
          setLoading(false);
          return;
        }

        const validation = await validateInvitationCode(invitationCode);
        if (!validation.valid) {
          setError('Invalid invitation code. Please check and try again.');
          setLoading(false);
          return;
        }

        const user = await register(email, password);
        
        // Hash transaction password
        const hashedTransactionPassword = await bcrypt.hash(transactionPassword, 10);
        
        // Create user profile in Firestore
        await createUserProfile(user.uid, {
          email: email,
          username: username,
          invitationCode: invitationCode,
          vipLevel: 'VIP1',
          creditScore: 100,
          balances: {
            wallet: 0,
            frozen: 0,
            commission: 0,
            total: 0,
            todaysProfit: 0,
            salary: 0
          }
        });

        // Add transaction password to user profile
        await updateUserData(user.uid, {
          transactionPassword: hashedTransactionPassword
        });

        // Show success and redirect to login
        setSuccess('Registration successful! Redirecting to login...');
        setTimeout(() => {
          setIsLogin(true);
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setUsername('');
          setInvitationCode('');
          setTransactionPassword('');
          setConfirmTransactionPassword('');
          setAcceptTerms(false);
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      let errorMessage = 'An error occurred. Please try again.';
      
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please login instead.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password.';
        loginAttemptsRef.current++;
        lastAttemptTimeRef.current = Date.now();
        localStorage.setItem('loginAttempts', loginAttemptsRef.current.toString());
        localStorage.setItem('lastAttemptTime', lastAttemptTimeRef.current.toString());
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password.';
        loginAttemptsRef.current++;
        lastAttemptTimeRef.current = Date.now();
        localStorage.setItem('loginAttempts', loginAttemptsRef.current.toString());
        localStorage.setItem('lastAttemptTime', lastAttemptTimeRef.current.toString());
      } else if (err.message && !err.message.includes('Firebase:')) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setUsername('');
    setInvitationCode('');
    setTransactionPassword('');
    setConfirmTransactionPassword('');
    setError('');
    setSuccess('');
    setAcceptTerms(false);
  };

  return (
    <div className="user-auth-page" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div className="card" style={{
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        borderRadius: '20px',
        border: 'none',
        overflow: 'hidden'
      }}>
        <div className="card-body p-4 p-md-5">
          {/* Header */}
          <div className="text-center mb-4">
            <div 
              className="auth-logo-container"
              style={{
                width: '200px',
                height: '80px',
                margin: '0 auto 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <img 
                src="/logos/logo.png" 
                alt="Circana Logo" 
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
            <h2 className="mb-2" style={{ fontWeight: 700, color: '#333' }}>
              {showForgotPassword ? 'Reset Password' : isLogin ? 'Login' : 'Create Account'}
            </h2>
            <p className="text-muted mb-0">
              {showForgotPassword 
                ? 'Enter your email to receive a password reset link'
                : isLogin 
                  ? 'Login to continue to your account' 
                  : 'Sign up to get started'}
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="alert alert-success d-flex align-items-center" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              <div>{success}</div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger d-flex align-items-start" role="alert">
              <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div>{error}</div>
            </div>
          )}

          {/* Forgot Password Form */}
          {showForgotPassword ? (
            <form onSubmit={handleForgotPassword}>
              <div className="mb-3">
                <label htmlFor="resetEmail" className="form-label fw-semibold">Email Address</label>
                <input
                  type="email"
                  className="form-control form-control-lg"
                  id="resetEmail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isLogin ? "your@email.com or username" : "your@email.com"}
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg w-100 mb-3"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-envelope me-2"></i>
                    Send Reset Link
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back to Login
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Registration Fields */}
              {!isLogin && (
                <>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label fw-semibold">
                      Username <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Choose a username"
                      required
                      disabled={loading}
                      minLength={3}
                    />
                    <small className="form-text text-muted">At least 3 characters</small>
                  </div>
                </>
              )}

              {/* Email */}
              <div className="mb-3">
                <label htmlFor="email" className="form-label fw-semibold">
                  {isLogin ? 'Email or Username' : 'Email Address'} <span className="text-danger">*</span>
                </label>
                <input
                  type={isLogin ? "text" : "email"}
                  className="form-control form-control-lg"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isLogin ? "your@email.com or username" : "your@email.com"}
                  required
                  disabled={loading}
                />
                {isLogin && (
                  <small className="form-text text-muted">You can login with either your email address or username</small>
                )}
              </div>

              {/* Password */}
              <div className="mb-3">
                <label htmlFor="password" className="form-label fw-semibold">
                  Password <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className="form-control form-control-lg"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isLogin ? "Enter your password" : "Create a strong password"}
                  required
                  disabled={loading}
                  minLength={isLogin ? 6 : 8}
                />
                {!isLogin && password && (
                  <PasswordStrengthIndicator password={password} />
                )}
                {!isLogin && (
                  <small className="form-text text-muted">
                    Must be at least 8 characters with uppercase, lowercase, number, and special character
                  </small>
                )}
              </div>

              {/* Confirm Password (Registration only) */}
              {!isLogin && (
                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label fw-semibold">
                    Confirm Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className="form-control form-control-lg"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    disabled={loading}
                    minLength={8}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <small className="form-text text-danger">Passwords do not match</small>
                  )}
                </div>
              )}

              {/* Transaction Password (Registration only) */}
              {!isLogin && (
                <>
                  <div className="mb-3">
                    <label htmlFor="transactionPassword" className="form-label fw-semibold">
                      Transaction Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="transactionPassword"
                      value={transactionPassword}
                      onChange={(e) => setTransactionPassword(e.target.value)}
                      placeholder="Enter transaction password"
                      required
                      disabled={loading}
                      minLength={6}
                    />
                    <small className="form-text text-muted">
                      Required for withdrawals and financial transactions. Must be at least 6 characters.
                    </small>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="confirmTransactionPassword" className="form-label fw-semibold">
                      Confirm Transaction Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="confirmTransactionPassword"
                      value={confirmTransactionPassword}
                      onChange={(e) => setConfirmTransactionPassword(e.target.value)}
                      placeholder="Confirm transaction password"
                      required
                      disabled={loading}
                      minLength={6}
                    />
                    {confirmTransactionPassword && transactionPassword !== confirmTransactionPassword && (
                      <small className="form-text text-danger">Transaction passwords do not match</small>
                    )}
                  </div>
                </>
              )}

              {/* Invitation Code (Registration only, required) */}
              {!isLogin && (
                <div className="mb-3">
                  <label htmlFor="invitationCode" className="form-label fw-semibold">
                    Invitation Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="invitationCode"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                    placeholder="Enter invitation code"
                    required
                    disabled={loading}
                    style={{ textTransform: 'uppercase' }}
                  />
                  <small className="form-text text-muted">
                    Enter your invitation code (optional).
                  </small>
                </div>
              )}

              {/* Terms and Conditions (Registration only) */}
              {!isLogin && (
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="acceptTerms"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      required
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="acceptTerms">
                      I accept the <a href="#" onClick={(e) => { e.preventDefault(); }} style={{ textDecoration: 'underline' }}>Terms and Conditions</a> <span className="text-danger">*</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Remember Me (Login only) */}
              {isLogin && (
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setError('');
                    }}
                    disabled={loading}
                    style={{ fontSize: '0.9rem' }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary btn-lg w-100 mb-3"
                disabled={loading || isLocked}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '12px',
                  fontWeight: 600
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {isLogin ? 'Logging in...' : 'Creating account...'}
                  </>
                ) : (
                  <>
                    <i className={`bi ${isLogin ? 'bi-box-arrow-in-right' : 'bi-person-plus'} me-2`}></i>
                    {isLogin ? 'Login' : 'Register'}
                  </>
                )}
              </button>

              {/* Toggle Login/Register */}
              <div className="text-center">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setShowForgotPassword(false);
                    resetForm();
                  }}
                  disabled={loading}
                  style={{ textDecoration: 'none' }}
                >
                  {isLogin ? (
                    <>
                      Don't have an account? <strong>Register here</strong>
                    </>
                  ) : (
                    <>
                      Already have an account? <strong>Login here</strong>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserAuth;
